import type { ButtonHTMLAttributes } from "react";

export interface SwitchProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "onChange" | "value"
> {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** Accessible label — required since the control carries no visible text
   * of its own (the caller places a label/description beside it). */
  "aria-label": string;
}

/**
 * A toggle switch — same on/off shape as a checkbox, but the platform's
 * settings surfaces (notification prefs, "which number clients see") all
 * read as instant-apply toggles rather than a form field with a Save
 * button, so a checkbox's small hit target and unclear affordance don't fit.
 *
 * Implemented as a native `<button role="switch">` rather than a styled
 * `<input type="checkbox">` — keyboard and screen-reader behavior for a
 * tri-state-free on/off control is simpler to get right starting from a
 * button than from fighting a checkbox's default rendering.
 */
export function Switch({
  checked,
  onCheckedChange,
  disabled,
  className,
  ...rest
}: SwitchProps) {
  return (
    <button
      {...rest}
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={["rp-switch", checked && "rp-switch--on", className]
        .filter(Boolean)
        .join(" ")}
    >
      <span className="rp-switch__thumb" />
    </button>
  );
}
